import React from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';

import Button from 'partial/components/Button';
import Form, { ErrorLabel } from 'partial/form/Form';
import FormPassword from 'partial/form/FormPassword';

import { ModalCard } from '../Modal';

const required = 'This field is required';

const validationSchema = yup.object().shape({
  old_password: yup.string().required(required),
  password: yup
    .string()
    .required(required)
    .min(8, 'At least 8 characters')
    .matches(/[A-Z]/, 'Must contain 1 uppercase letter')
    .matches(/[a-z]/, 'Must contain 1 lowercase letter')
    .matches(/[0-9]/, 'Must contain 1 numeric digit')
    .matches(/^(?=.*[!@#$%^&*])/, 'At least 1 special character (!@#$%&*)'),
  password_confirmation: yup
    .string()
    .required(required)
    .oneOf([yup.ref('password')], 'Password does not match'),
});

function ChangePasswordModal({ submit, isLoading, onClose }) {
  const [validateOnChange, setValidateOnChange] = React.useState(false);
  const { setFieldValue, handleSubmit, values, errors } = useFormik({
    validationSchema,
    initialValues: {
      old_password: '',
      password: '',
      password_confirmation: '',
    },
    onSubmit: (form, { setFieldError }) => {
      submit(form, onClose, ({ response }) => {
        const err_message = response?.data?.message;
        setFieldError('old_password', err_message);
      });
    },
    validateOnChange,
  });

  return (
    <ModalCard title="Change Password" size="sm" onClose={onClose}>
      <Form onSubmit={handleSubmit} error={errors} className="p-6">
        <div className="flex flex-col w-full space-y-4">
          <div>
            <FormPassword
              name="old_password"
              label="Password"
              placeholder="Enter Current Password"
              onSetFieldValue={setFieldValue}
              value={values?.old_password || ''}
              error={errors?.old_password || ''}
              required
            />
            <ErrorLabel name="old_password" />
          </div>
          <div>
            <FormPassword
              name="password"
              label="New Password"
              placeholder="Enter New Password"
              onSetFieldValue={setFieldValue}
              value={values?.password || ''}
              error={errors?.password || ''}
              required
            />
            <ErrorLabel name="password" />
          </div>
          <div>
            <FormPassword
              name="password_confirmation"
              label="Confirm Password"
              placeholder="Confirm New Password"
              onSetFieldValue={setFieldValue}
              value={values?.password_confirmation || ''}
              error={errors?.password_confirmation || ''}
              required
            />
            <ErrorLabel name="password_confirmation" />
          </div>
        </div>
        <div className="flex justify-center w-full mt-6">
          <Button
            type="button"
            onClick={onClose}
            isLoading={isLoading}
            className="w-52"
          >
            Cancel
          </Button>
          <Button
            primary
            type="submit"
            className="w-52"
            isLoading={isLoading}
            onClick={() => setValidateOnChange(true)}
          >
            Confirm
          </Button>
        </div>
      </Form>
    </ModalCard>
  );
}

ChangePasswordModal.propTypes = {
  onClose: PropTypes.instanceOf(Function).isRequired,
  submit: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool,
};

ChangePasswordModal.defaultProps = {
  isLoading: false,
};

export default ChangePasswordModal;
